/* eslint-disable no-console */

export const DEBUG_LEVELS = ['log', 'error', 'warn', 'info', 'debug'] as const;
export type DebugLevel = typeof DEBUG_LEVELS[number];
// @ts-ignore
const ORIGINAL_FUNCTIONS: Record<DebugLevel, (...args: any[]) => void> = DEBUG_LEVELS.reduce((acc, level) => {
  // @ts-ignore
  acc[level] = console[level];
  return acc;
}, {});

type DebugEntry = {
  level: DebugLevel;
  args: any[];
  date: Date;
};
let DEBUG_LOGS: DebugEntry[] = [];

const SUPPRESSED_DEBUG_TRACE_PREFIXES = [
  '[init-generation-trace]',
  '[post-auth-trace]',
  '[public-google-auth-trace]',
  '[reconnect-trace]',
] as const;

function shouldSuppressDebugConsoleEntry(level: DebugLevel, args: any[]) {
  if (!args.length || (level !== 'info' && level !== 'debug' && level !== 'log')) {
    return false;
  }

  const [firstArg] = args;
  return typeof firstArg === 'string'
    && SUPPRESSED_DEBUG_TRACE_PREFIXES.some((prefix) => firstArg.startsWith(prefix));
}

function createDebugLogReplacer() {
  const seen = new WeakSet<object>();

  return (_key: string, value: unknown) => {
    if (typeof value === 'bigint') {
      return value.toString();
    }

    if (value instanceof Error) {
      return {
        name: value.name,
        message: value.message,
        stack: value.stack,
      };
    }

    if (typeof Element !== 'undefined' && value instanceof Element) {
      return {
        tagName: value.tagName,
        id: value.id || undefined,
        className: value.className || undefined,
      };
    }

    if (typeof value === 'function') {
      return `[Function ${value.name || 'anonymous'}]`;
    }

    if (value && typeof value === 'object') {
      if (seen.has(value as object)) {
        return '[Circular]';
      }

      seen.add(value as object);
    }

    return value;
  };
}

export function logDebugMessage(level: DebugLevel, ...args: any[]) {
  if (shouldSuppressDebugConsoleEntry(level, args)) {
    return;
  }

  DEBUG_LOGS.push({
    level,
    args,
    date: new Date(),
  });
  ORIGINAL_FUNCTIONS[level](...args);
}

export function initDebugConsole() {
  DEBUG_LOGS = [];
  DEBUG_LEVELS.forEach((level) => {
    // @ts-ignore
    console[level] = (...args: any[]) => {
      logDebugMessage(level, ...args);
    };
  });
}

export function disableDebugConsole() {
  DEBUG_LEVELS.forEach((level) => {
    // @ts-ignore
    console[level] = ORIGINAL_FUNCTIONS[level];
  });
  DEBUG_LOGS = [];
}

export function getDebugLogs() {
  return JSON.stringify(DEBUG_LOGS, createDebugLogReplacer());
}
